import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { z } from 'zod';

import { getCalendarEventsBetween } from '~/database/supabase/calendar/queries.server.ts';
import { getUserCompanyIds } from '~/database/supabase/instrument_company/queries.server.ts';
import { getHints } from '~/utils/client-hints.tsx';
import { transformCalendarEvents } from '~/views/calendar/components/transform-calendar-events.ts';
import { getMonthFromEpoch, getMonthPeriod, getNumberOfMonthsToDisplay } from '~/views/calendar/utils/calendar.ts';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';

const GetCalendarPageDataSchema = z.object({
	epoch: z.string().nullish(),
});

export interface CalendarPageData {
	readonly events: ReturnType<typeof transformCalendarEvents>;
	readonly numberOfMonths: number;
	readonly userLocale: string;
}

export const getCalendarPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: ['auth.session', 'supabase.calendar.events', 'supabase.user-company-ids'],
			relatesTo: 'calendar.page',
		}),
	])
	.inputValidator(GetCalendarPageDataSchema)
	.handler(async ({ data }) => {
		const request = getRequest();
		const authSession = await requireStartAuthSession({ request });
		const userLocale = getHints(request).locale;
		const searchDate = getMonthFromEpoch(data.epoch ?? null);
		const numberOfMonths = getNumberOfMonthsToDisplay(request);
		const dates = searchDate ? getMonthPeriod(searchDate) : getMonthPeriod();

		const [calendarEvents, userCompanies] = await Promise.all([
			getCalendarEventsBetween(dates.startDate, dates.endDate),
			getUserCompanyIds({ userId: authSession.userId }),
		]);

		return {
			events: transformCalendarEvents(calendarEvents, userCompanies),
			numberOfMonths,
			userLocale,
		} satisfies CalendarPageData;
	});
